import React, { useCallback, useEffect, useState } from "react";
import { FolderOpen, X, Loader2, Lock } from "lucide-react";
import { DocumentChip, listConsultationDocuments } from "@shared/consultation/ConsultationDocuments";

// Body listing every document shared on this consultation (booking + chat).
// Used as the Documents tab of the workspace right panel (and by the legacy drawer).
export function DocumentsPane({ cid, refreshKey, active = true, onCount }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await listConsultationDocuments(cid)); } catch { setData({ items: [], usage: {} }); }
  }, [cid]);
  useEffect(() => { if (active) load(); }, [active, load, refreshKey]);
  const items = data?.items || [];
  useEffect(() => { if (data) onCount?.(items.length); }, [data, items.length, onCount]);
  const booking = items.filter((d) => d.source === "booking");
  const chat = items.filter((d) => d.source !== "booking");
  return (
    <div className="box-border flex w-full max-w-full min-w-0 flex-1 min-h-0 overflow-y-auto p-4 space-y-5" data-testid="documents-pane">
      {!data ? <div className="grid place-items-center py-10 text-slate-400"><Loader2 className="animate-spin" /></div> : (
        <>
          <p className="text-xs font-bold text-slate-500" data-testid="documents-count">({items.length}) document{items.length === 1 ? "" : "s"}</p>
          {data.uploads_open === false && (
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 inline-flex items-center gap-1" data-testid="documents-readonly">
              <Lock size={12} /> Consultation closed — documents are read-only.
            </p>
          )}
          <Section title="Attached at booking" docs={booking} cid={cid} empty="No documents were attached at booking." />
          <Section title="Shared in chat" docs={chat} cid={cid} empty="Nothing shared in chat yet." />
          {data.usage?.max_files && (
            <p className="text-[11px] text-slate-400" data-testid="documents-usage">
              {data.usage.files}/{data.usage.max_files} files · {(data.usage.bytes / 1024 / 1024).toFixed(1)}/{Math.round(data.usage.max_bytes / 1024 / 1024)} MB used
            </p>
          )}
        </>
      )}
    </div>
  );
}

// Legacy slide-over wrapper (kept for any caller that still wants a drawer).
export function DocumentsDrawer({ cid, open, onClose, refreshKey }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="documents-drawer">
      <button type="button" aria-label="Close documents" className="flex-1 bg-black/30" onClick={onClose} />
      <aside className="box-border flex h-full w-full max-w-sm min-w-0 flex-col bg-white shadow-2xl">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="font-extrabold text-[#0A2540] inline-flex items-center gap-2" style={{ fontFamily: "Poppins" }}>
            <FolderOpen size={16} /> Documents
          </p>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="documents-close" className="text-slate-400 hover:text-[#0A2540]"><X size={18} /></button>
        </div>
        <DocumentsPane cid={cid} refreshKey={refreshKey} active={open} />
      </aside>
    </div>
  );
}

function Section({ title, docs, cid, empty }) {
  return (
    <div className="w-full min-w-0">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">{title}</p>
      {docs.length === 0 ? <p className="text-xs text-slate-400">{empty}</p> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id}>
              <DocumentChip cid={cid} doc={d} />
              <p className="text-[10px] text-slate-400 mt-0.5 ml-1">{d.uploader_role === "lawyer" ? "Lawyer" : "Client"} · {new Date(d.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
